import {useCallback, useEffect, useMemo, useRef} from 'react';
import {getMessage, getMessageQuotePreview} from '../../../service/ChatService';
import {applyQuotePreview, mapWithConcurrency, mergeMessages, messageEventId, messagesReferenceMessage} from '../chatUtils';
import {createRefreshQueue} from '../refreshQueue';
import {limitMessageRefresh} from '../messageRefreshLimiter';

function mergeEvents(left, right) {
    const events = new Map();
    for (const event of [...left, ...right]) {
        const id = messageEventId(event);
        const previous = events.get(id);
        if (id && (!previous || Number(event.event_id || 0) >= Number(previous.event_id || 0))) events.set(id, event);
    }
    return [...events.values()];
}

// Message changes are serialized separately from timeline pagination. Ordinary
// events never cancel an in-flight batch; each affected root is fetched once.
export default function useMessageMutations({events, messages, messagesRef, setMessages, setError, owner, conversationId, actorId}) {
    const applied = useRef(new Map());
    const readBarrier = useRef(0);
    const queue = useMemo(() => createRefreshQueue(async (incoming, signal) => {
        const barrier = readBarrier.current;
        const pending = incoming.filter(event => {
            const id = messageEventId(event);
            const key = `${event.event_id || event.occurred_at || ''}:${event.data?.version || ''}`;
            return applied.current.get(id) !== key && (messagesReferenceMessage(messagesRef.current, id)
                || messagesRef.current.some(message => message.id === id || message.id === Number(event.data?.thread_root_message_id)));
        });
        const messageRequests = new Map();
        const quoteRequests = new Map();
        const once = (requests, id, fetch) => {
            if (!requests.has(id)) requests.set(id, limitMessageRefresh(() => fetch(id, signal), signal));
            return requests.get(id);
        };
        const patches = await mapWithConcurrency(pending, 4, async event => {
            const id = messageEventId(event);
            const target = messagesRef.current.some(message => message.id === id) ? id : Number(event.data?.thread_root_message_id);
            let message = null;
            if (target && messagesRef.current.some(item => item.id === target)) {
                try { message = (await once(messageRequests, target, getMessage)).message; }
                catch (failure) { if (failure.status !== 404) throw failure; }
            }
            const quote = messagesReferenceMessage(messagesRef.current, id)
                ? (await once(quoteRequests, id, getMessageQuotePreview)).quoted_message : null;
            return {event, id, target, message, quote};
        });
        if (signal.aborted || !owner.mounted) return;
        setError(null);
        patches.forEach(({event, id}) => applied.current.set(id, `${event.event_id || event.occurred_at || ''}:${event.data?.version || ''}`));
        if (applied.current.size > 128) {
            for (const id of [...applied.current.keys()].slice(0, applied.current.size - 128)) applied.current.delete(id);
        }
        setMessages(current => patches.reduce((result, {event, id, message, quote}) => {
            let next = result;
            if (message) {
                const patch = {...message};
                // A count fetched before a confirmed read cannot undo that
                // read; its targeted follow-up will supply the fresh count.
                if (barrier < readBarrier.current) patch.unread_reply_count = next.find(item => item.id === patch.id)?.unread_reply_count;
                next = mergeMessages(next, [patch]);
            } else if (event.type === 'message.deleted') next = next.filter(item => item.id !== id);
            return quote ? applyQuotePreview(next, quote, event) : next;
        }, current));
    }, mergeEvents, {minIntervalMs: 200, retry: true}), [messagesRef, owner, setMessages, setError]);

    useEffect(() => () => { queue.cancel(); applied.current.clear(); readBarrier.current = 0; }, [queue]);
    useEffect(() => {
        const incoming = Object.values(events || {});
        for (const event of incoming) {
            if (event.type === 'conversation.read') readBarrier.current = Math.max(readBarrier.current, Number(event.event_id) || 0);
        }
        if (incoming.length) void queue.request(incoming).catch(error => { if (error?.name !== 'AbortError') setError(error); });
    }, [events, messages, queue, setError]);

    useEffect(() => {
        const confirmed = ({detail}) => {
            if (Number(detail?.actor_id) !== Number(actorId) || Number(detail.conversation_id) !== Number(conversationId)) return;
            readBarrier.current = Math.max(readBarrier.current, Number(detail.event_id) || 0);
            const rootId = Number(detail.root_message_id);
            if (rootId) void queue.request([{type: 'conversation.read', event_id: detail.event_id, data: {message_id: rootId, thread_root_message_id: rootId}}])
                .catch(error => { if (error?.name !== 'AbortError') setError(error); });
        };
        window.addEventListener('chat-read-confirmed', confirmed);
        return () => window.removeEventListener('chat-read-confirmed', confirmed);
    }, [actorId, conversationId, queue, setError]);

    return useCallback(messageId => queue.request([{type: 'message.updated', occurred_at: crypto.randomUUID(), data: {message_id: messageId}}]), [queue]);
}
